import { Suspense } from 'react';

import { isImpersonating, useTokenStore } from '@shared/auth/token.store';
import { useAuth } from '@shared/auth/use-auth';

import { useEnpsSurvey } from '../hooks/use-enps-survey';

import { EnpsSurveyModal } from './EnpsSurveyModal';

/**
 * Mounted once inside the authenticated app shell: shows the eNPS survey on
 * login when the backend says one is due. A superadmin impersonating a tenant
 * user is never surveyed on that user's behalf (the backend enforces this
 * too; skipping the request here just avoids a pointless round trip).
 */
export function EnpsSurveyHost() {
  const { user, isAuthenticated } = useAuth();
  const accessToken = useTokenStore((state) => state.accessToken?.token);
  const enabled = isAuthenticated && !!accessToken && !isImpersonating();
  const { survey, open, setOpen, submit, dismiss } = useEnpsSurvey(user?.id ?? null, enabled);

  if (!survey) return null;

  // The hook preloads the `enps` namespace before opening; this boundary is a
  // safety net so a late translation load can never suspend the whole app.
  return (
    <Suspense fallback={null}>
      <EnpsSurveyModal
        open={open}
        onOpenChange={setOpen}
        onSubmit={submit}
        onSkip={dismiss}
        minScore={survey.minScore}
        maxScore={survey.maxScore}
        commentMaxLength={survey.commentMaxLength}
      />
    </Suspense>
  );
}
